/**
 * Admin -> Users (eskiz 22): foydalanuvchilar, yaratish (login + vaqtinchalik parol; email yo'q, 2026-10-18), tahrirlash,
 * yangi parol berish, bloklash va driverlarni o'tkazish. Owner hammani, filial menejeri faqat o'z filialini ko'radi (backend scope).
 */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Copy, Dices, MoreHorizontal } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import type { Me, Role } from "@/features/auth/types";
import { useUserOptions } from "@/features/drivers/api";
import { useBlockUser, useBranches, useCreateUser, useReset2fa, useSetUserPassword, useUnblockUser, useUpdateUser, useUsers } from "@/features/admin/api";
import { generatePassword, isValidUsername, normalizeUsername, suggestUsername } from "@/features/admin/usernames";
import type { AdminUser, UserWrite } from "@/features/admin/types";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox, Field, FilterSelect, Input, Select } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { EmptyState, ErrorState, PageLoading, SimilarNote } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { ContextMenu, DropdownMenu, type MenuEntry } from "@/components/ui/context-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { callPhone, copyText } from "@/lib/clipboard";
import { hasAccessSettings, userMenu } from "@/features/admin/menu";
import { AccessModal } from "./AccessModal";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";

const STAFF_ROLES: Role[] = ["owner", "branch_manager", "recruiter"];

export function UsersPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Users me={me.data} />;
}

function Users({ me }: { me: Me }) {
  const { t } = useTranslation("admin");
  const isOwner = me.role === "owner";
  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const role = params.get("role") ?? "";
  const branch = params.get("branch") ?? "";
  const status = params.get("status") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "users");
  const [createOpen, setCreateOpen] = useState(params.get("new") === "1"); // ⌘K / People sahifasidan "Yangi xodim"
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [blocking, setBlocking] = useState<AdminUser | null>(null);
  const [accessFor, setAccessFor] = useState<AdminUser | null>(null);
  const [passwordFor, setPasswordFor] = useState<AdminUser | null>(null);
  // Yaratilgan yoki yangi parol berilgan xodimning kirish ma'lumotlari -- bir marta ko'rsatiladi
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const navigate = useNavigate();
  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const unblock = useUnblockUser();
  const reset2fa = useReset2fa();
  const runAction = (promise: Promise<unknown>, success: string): Promise<void> =>
    promise.then(
      () => void toast.success(success),
      (err) => void toast.error(err instanceof ApiError ? err.message : t("users.actionFailed")),
    );
  const menuFor = (u: AdminUser) =>
    userMenu(
      u,
      me,
      {
        navigate: (path) => navigate(path),
        copy: (text) => void copyText(text),
        call: callPhone,
        profile: personNav,
        edit: () => setEditing(u),
        access: () => setAccessFor(u),
        block: () => setBlocking(u),
        unblock: () => void runAction(unblock.mutateAsync(u.id), t("users.unblocked", { name: u.display_name })),
        setPassword: () => setPasswordFor(u),
        reset2fa: () =>
          void confirmDialog({ title: tm("user.reset2faTitle", { name: u.display_name }), description: tm("user.reset2faDesc"), confirmLabel: tm("user.reset2fa"), danger: true }).then(
            (ok) => {
              if (ok) void runAction(reset2fa.mutateAsync(u.id), t("users.twofaReset", { name: u.display_name }));
            },
          ),
      },
      tm,
    );

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const users = useUsers({
    search: search || undefined,
    role: role || undefined,
    branch: branch || undefined,
    is_blocked: status === "blocked" ? "true" : status === "active" ? "false" : undefined,
    page,
    page_size: pageSize,
    ordering: "first_name",
  });
  const branches = useBranches();
  const hasFilters = !!(search || role || branch || status);

  return (
    <AdminLayout
      title={t("users.title")}
      subtitle={users.data && branches.data ? t("users.subtitle", { count: users.data.count, branches: branches.data.count }) : "…"}
      actions={
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          {t("users.newUser")}
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setParam("search", e.target.value)} placeholder={t("users.searchPlaceholder")} className="w-[240px]" aria-label={t("users.searchPlaceholder")} />
        <FilterSelect value={role} onChange={(e) => setParam("role", e.target.value)} className="w-auto min-w-[160px] max-w-[280px]" aria-label={t("users.col.role")}>
          <option value="">{t("users.roleAll")}</option>
          {STAFF_ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`common:roles.${r}`)}
            </option>
          ))}
        </FilterSelect>
        {(isOwner || me.scope_branches.length > 1) && (
          <FilterSelect value={branch} onChange={(e) => setParam("branch", e.target.value)} className="w-auto min-w-[160px] max-w-[280px]" aria-label={t("users.col.branch")}>
            <option value="">{t("users.branchAll")}</option>
            {branches.data?.results.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </FilterSelect>
        )}
        <FilterSelect value={status} onChange={(e) => setParam("status", e.target.value)} className="w-auto min-w-[156px] max-w-[280px]" aria-label={t("users.col.status")}>
          <option value="">{t("users.statusAll")}</option>
          <option value="active">{t("users.active")}</option>
          <option value="blocked">{t("users.blocked")}</option>
        </FilterSelect>
        {hasFilters && (
          <Button variant="link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            {t("common:actions.clearFilters")}
          </Button>
        )}
      </div>

      {users.isPending ? (
        <PageLoading />
      ) : users.isError ? (
        <ErrorState message={users.error.message} onRetry={() => users.refetch()} />
      ) : users.data.results.length === 0 ? (
        <EmptyState title={hasFilters ? t("users.emptyFiltered") : t("users.emptyTitle")} description={t("users.emptyDesc")} />
      ) : (
        <>
          <SimilarNote show={users.data.fuzzy} />
          <Table>
            <thead>
              <tr>
                <Th>{t("users.col.name")}</Th>
                <Th>{t("users.col.role")}</Th>
                <Th>{t("users.col.branch")}</Th>
                <Th className="text-right">{t("users.col.drivers")}</Th>
                <Th>{t("users.col.lastLogin")}</Th>
                <Th>{t("users.col.twofa")}</Th>
                <Th>{t("users.col.status")}</Th>
                <Th className="text-right">{t("users.col.actions")}</Th>
              </tr>
            </thead>
            <tbody>
              {users.data.results.map((u) => (
                <ContextMenu key={u.id} items={() => menuFor(u)}>
                  <Tr className={u.is_blocked ? "opacity-70" : undefined}>
                    <Td>
                      <div className="leading-tight">
                        <div className="font-semibold">
                          <PersonLink id={u.id} name={u.full_name} role={u.role} />
                        </div>
                        <div className="text-[11px] text-muted">@{u.username}</div>
                      </div>
                    </Td>
                    <Td>
                      {t(`common:roles.${u.role}`)}
                      {u.custom_access && (
                        <Badge tone="orange" className="ml-2" title={t("access.customHint")}>
                          {t("access.custom")}
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      {u.branch_name ?? <span className="text-muted">—</span>}
                      {u.extra_branch_names.length > 0 && <div className="text-[11px] text-muted">{t("users.alsoIn", { names: u.extra_branch_names.join(", ") })}</div>}
                    </Td>
                    <Td className="text-right">{u.drivers_count}</Td>
                    <Td className="text-muted">{u.last_login ? formatWhen(u.last_login) : t("users.never")}</Td>
                    <Td>
                      <Badge tone={u.totp_enabled ? "green" : "grey"}>{u.totp_enabled ? t("users.on") : t("users.off")}</Badge>
                    </Td>
                    <Td>
                      <Badge tone={u.is_blocked ? "red" : "green"}>{u.is_blocked ? t("users.blocked") : t("users.active")}</Badge>
                    </Td>
                    <Td>
                      <RowActions user={u} me={me} onEdit={() => setEditing(u)} onAccess={() => setAccessFor(u)} onBlock={() => setBlocking(u)} menu={() => menuFor(u)} />
                    </Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <Pagination
            page={page}
            pages={users.data.pages}
            count={users.data.count}
            pageSize={pageSize}
            onPage={(p) => setParam("page", String(p))}
            onPageSize={(s) => {
              rememberPageSize("users", s);
              setParam("page_size", String(s));
            }} label={t("users.users")} />
        </>
      )}

      <UserFormModal open={createOpen} onOpenChange={setCreateOpen} me={me} onCreated={setCredentials} />
      <UserFormModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} me={me} user={editing} />
      <BlockModal user={blocking} onOpenChange={(o) => !o && setBlocking(null)} />
      <AccessModal user={accessFor} onOpenChange={(o) => !o && setAccessFor(null)} />
      <SetPasswordModal user={passwordFor} onOpenChange={(o) => !o && setPasswordFor(null)} onDone={setCredentials} />
      <CredentialsDialog data={credentials} onOpenChange={(o) => !o && setCredentials(null)} />
    </AdminLayout>
  );
}

// ---- Qator amallari ----

/** Qator amallari: tez havolalar + "⋯" (o'ng tugma menyusi bilan bir xil elementlar, portal'da -- jadval kesmaydi) */
function RowActions({ user, me, onEdit, onAccess, onBlock, menu }: { user: AdminUser; me: Me; onEdit: () => void; onAccess: () => void; onBlock: () => void; menu: () => MenuEntry[] }) {
  const { t } = useTranslation("admin");
  const unblock = useUnblockUser();
  const isSelf = user.id === me.id;
  const canManage = me.role === "owner" || (user.role !== "owner" && user.role !== "branch_manager");

  async function run(action: () => Promise<unknown>, success: string) {
    try {
      await action();
      toast.success(success);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("users.actionFailed"));
    }
  }

  return (
    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
      <Button size="sm" variant="link" onClick={onEdit} disabled={!canManage}>
        {t("common:actions.edit")}
      </Button>
      <span className="text-muted">·</span>
      {hasAccessSettings(user, me) && (
        <>
          <Button size="sm" variant="link" onClick={onAccess}>
            {t("access.open")}
          </Button>
          <span className="text-muted">·</span>
        </>
      )}
      {user.is_blocked ? (
        <Button size="sm" variant="link" onClick={() => run(() => unblock.mutateAsync(user.id), t("users.unblocked", { name: user.display_name }))} disabled={!canManage}>
          {t("users.unblock")}
        </Button>
      ) : (
        <Button size="sm" variant="link" onClick={onBlock} disabled={!canManage || isSelf} title={isSelf ? t("users.cannotBlockSelf") : undefined}>
          {t("users.block")}
        </Button>
      )}
      <DropdownMenu items={menu}>
        <button type="button" className="rounded p-1 text-muted hover:bg-fill hover:text-ink data-[state=open]:bg-fill data-[state=open]:text-ink" aria-label={t("users.moreActions")}>
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenu>
    </div>
  );
}

// ---- Yaratish / tahrirlash oynasi ----

interface FormState {
  first_name: string;
  last_name: string;
  username: string;
  /** Vaqtinchalik parol -- faqat yaratishda */
  password: string;
  role: Role;
  branch: string;
  /** Qo'shimcha doira: menejer -- boshqa filiallar (asosiysidan tashqari) */
  extra_branches: number[];
  phone: string;
}

const EMPTY: FormState = { first_name: "", last_name: "", username: "", password: "", role: "recruiter", branch: "", extra_branches: [], phone: "" };

function fromUser(u: AdminUser): FormState {
  return {
    first_name: u.first_name, last_name: u.last_name, username: u.username, password: "", role: u.role, branch: u.branch ? String(u.branch) : "",
    extra_branches: u.extra_branches, phone: u.phone,
  };
}

/** Kirish ma'lumotlari (login + vaqtinchalik parol): yaratgandan yoki yangi parol bergandan keyin bir marta ko'rsatiladi */
export interface Credentials {
  name: string;
  username: string;
  password: string;
}

export function UserFormModal({ open, onOpenChange, me, user, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; me: Me; user?: AdminUser | null; onCreated?: (c: Credentials) => void }) {
  const { t } = useTranslation("admin");
  const editing = !!user;
  const isOwner = me.role === "owner";
  const [form, setForm] = useState<FormState>(EMPTY);
  // Login ism-familiyadan taklif qilinadi, qo'lda o'zgartirilgach taklif to'xtaydi
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const create = useCreateUser();
  const update = useUpdateUser();
  const branches = useBranches();
  // Owner -- istalgan filial; menejer -- o'z filiallari (bittasi bo'lsa -- qulflangan)
  const branchChoices = isOwner ? (branches.data?.results ?? []) : me.scope_branches;
  const branchId = form.branch;
  const busy = create.isPending || update.isPending;
  const roleOptions: Role[] = isOwner ? STAFF_ROLES : ["recruiter"];
  const extraBranchOptions = (branches.data?.results ?? []).filter((b) => String(b.id) !== form.branch).map((b) => ({ id: b.id, label: b.name }));

  useEffect(() => {
    if (open) {
      setForm(user ? fromUser(user) : { ...EMPTY, branch: !isOwner && me.branch ? String(me.branch.id) : "" });
      setUsernameEdited(!!user);
      setErrors({});
    }
  }, [open, user, isOwner, me.branch]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setName = (key: "first_name" | "last_name", value: string) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (!usernameEdited) next.username = suggestUsername(next.first_name, next.last_name);
      return next;
    });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    const local: Record<string, string[]> = {};
    if (!form.first_name.trim()) local.first_name = [t("users.form.firstNameRequired")];
    if (!isValidUsername(form.username)) local.username = [t("users.form.usernameInvalid")];
    if (!editing && form.password.length < 10) local.password = [t("users.form.passwordShort")];
    if (form.role !== "owner" && !branchId) local.branch = [t("users.form.branchRequired")];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    const payload: UserWrite = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      username: normalizeUsername(form.username),
      role: form.role,
      branch: form.role === "owner" ? null : branchId ? Number(branchId) : null,
      // Asosiy filial qo'shimchada takrorlanmaydi; boshqa rolda -- bo'sh (backend ham tozalaydi)
      extra_branches: form.role === "branch_manager" && isOwner ? form.extra_branches.filter((id) => String(id) !== form.branch) : [],
      phone: form.phone.trim(),
    };
    // Menejer menejerga filial biriktirmaydi (faqat owner): maydon yuborilmaydi
    if (!isOwner) delete payload.extra_branches;
    try {
      if (editing && user) {
        await update.mutateAsync({ id: user.id, version: user.version, ...payload });
        toast.success(t("users.form.saved"));
      } else {
        // Parol faqat yaratishda yuboriladi; keyin -- "Yangi parol berish"
        await create.mutateAsync({ ...payload, password: form.password });
        toast.success(t("users.form.created"));
        onCreated?.({ name: `${payload.first_name} ${payload.last_name}`.trim(), username: payload.username, password: form.password });
      }
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) {
          toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
          onOpenChange(false);
          return;
        }
        if (err.fields) setErrors(err.fields);
        else toast.error(err.message);
      } else toast.error(t("users.form.saveFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("users.form.editTitle", { name: user?.full_name ?? "" }) : t("users.form.newTitle")}
      description={editing ? undefined : t("users.form.newDesc")}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? t("common:actions.saveChanges") : t("users.form.create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("users.form.firstName")} required error={errors.first_name}>
            <Input value={form.first_name} onChange={(e) => setName("first_name", e.target.value)} autoFocus aria-invalid={!!errors.first_name} />
          </Field>
          <Field label={t("users.form.lastName")} error={errors.last_name}>
            <Input value={form.last_name} onChange={(e) => setName("last_name", e.target.value)} />
          </Field>
        </div>
        <div className={editing ? undefined : "grid grid-cols-2 gap-3"}>
          <Field label={t("users.form.username")} required error={errors.username} hint={errors.username ? undefined : t("users.form.usernameInvalid")}>
            <Input
              value={form.username}
              onChange={(e) => {
                setUsernameEdited(true);
                set("username", e.target.value);
              }}
              placeholder={t("users.form.usernamePlaceholder")}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={!!errors.username}
            />
          </Field>
          {!editing && (
            <Field label={t("users.form.password")} required error={errors.password} hint={errors.password ? undefined : t("users.form.passwordHint")}>
              <PasswordInput value={form.password} onChange={(v) => set("password", v)} randomLabel={t("users.form.random")} invalid={!!errors.password} />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("users.form.role")} required error={errors.role} hint={user?.custom_access && form.role !== user.role ? t("access.roleChangeHint") : undefined}>
            <Select value={form.role} onChange={(e) => set("role", e.target.value as Role)}>
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {t(`common:roles.${r}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("users.form.phone")} error={errors.phone}>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+998 90 123 45 67" />
          </Field>
        </div>
        {form.role !== "owner" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("users.form.branch")} required error={errors.branch}>
              {isOwner || branchChoices.length > 1 ? (
                <Select value={form.branch} onChange={(e) => set("branch", e.target.value)} aria-invalid={!!errors.branch}>
                  <option value="">{t("users.form.chooseBranch")}</option>
                  {branchChoices.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input value={branchChoices.find((b) => String(b.id) === form.branch)?.name ?? me.branch?.name ?? ""} disabled />
              )}
            </Field>
          </div>
        )}
        {form.role === "branch_manager" && isOwner && (
          <Field label={t("users.form.extraBranches")} error={errors.extra_branches} hint={t("users.form.extraBranchesHint")}>
            <CheckList options={extraBranchOptions} value={form.extra_branches} onChange={(v) => set("extra_branches", v)} label={t("users.form.extraBranches")} empty={t("users.form.nothingToChoose")} />
          </Field>
        )}
      </form>
    </Modal>
  );
}

/** Vaqtinchalik parol maydoni: ochiq ko'rinadi (yaratuvchi uni xodimga topshiradi), "Tasodifiy" tugmasi 12 belgili parol beradi */
function PasswordInput({ value, onChange, randomLabel, invalid }: { value: string; onChange: (v: string) => void; randomLabel: string; invalid?: boolean }) {
  return (
    <div className="flex gap-2">
      <Input value={value} onChange={(e) => onChange(e.target.value)} autoComplete="new-password" autoCapitalize="none" spellCheck={false} className="font-mono" aria-invalid={invalid} />
      <Button type="button" onClick={() => onChange(generatePassword())} title={randomLabel} aria-label={randomLabel}>
        <Dices size={16} strokeWidth={1.75} />
        <span className="hidden sm:inline">{randomLabel}</span>
      </Button>
    </div>
  );
}

/** Yangi (vaqtinchalik) parol berish: email yo'q, parolni unutgan xodimga owner/menejer shu yerdan beradi */
export function SetPasswordModal({ user, onOpenChange, onDone }: { user: AdminUser | null; onOpenChange: (o: boolean) => void; onDone: (c: Credentials) => void }) {
  const { t } = useTranslation("admin");
  const setPassword = useSetUserPassword();
  const [password, setPassword_] = useState("");
  const [error, setError] = useState<string[] | undefined>();

  useEffect(() => {
    if (user) {
      setPassword_(generatePassword());
      setError(undefined);
    }
  }, [user]);

  if (!user) return null;

  async function submit() {
    if (!user) return;
    if (password.length < 10) {
      setError([t("users.form.passwordShort")]);
      return;
    }
    try {
      await setPassword.mutateAsync({ id: user.id, password });
      toast.success(t("users.passwordSet", { name: user.display_name }));
      onOpenChange(false);
      onDone({ name: user.full_name, username: user.username, password });
    } catch (err) {
      if (err instanceof ApiError && err.fields?.password) setError(err.fields.password);
      else toast.error(err instanceof ApiError ? err.message : t("users.setPasswordModal.failed"));
    }
  }

  return (
    <Modal
      open={!!user}
      onOpenChange={onOpenChange}
      title={t("users.setPasswordModal.title", { name: user.full_name })}
      description={t("users.setPasswordModal.desc")}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={setPassword.isPending} onClick={submit}>
            {t("users.setPasswordModal.save")}
          </Button>
        </>
      }
    >
      <Field label={t("users.setPasswordModal.password")} required error={error}>
        <PasswordInput value={password} onChange={setPassword_} randomLabel={t("users.setPasswordModal.random")} invalid={!!error} />
      </Field>
    </Modal>
  );
}

/** Kirish ma'lumotlari -- bir marta: login va vaqtinchalik parol, nusxalash tugmalari bilan */
export function CredentialsDialog({ data, onOpenChange }: { data: Credentials | null; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation("admin");
  if (!data) return null;
  const row = (label: string, value: string) => (
    <div className="flex items-center justify-between gap-3 rounded-[10px] bg-fill px-3 py-2">
      <div className="min-w-0">
        <div className="text-[11px] text-muted">{label}</div>
        <div className="truncate font-mono text-[14px] font-semibold">{value}</div>
      </div>
      <Button size="sm" onClick={() => void copyText(value)} aria-label={`${t("common:actions.copy")}: ${label}`}>
        <Copy size={14} strokeWidth={1.75} />
      </Button>
    </div>
  );
  return (
    <Modal
      open={!!data}
      onOpenChange={onOpenChange}
      title={t("users.credentials.title", { name: data.name })}
      description={t("users.credentials.desc")}
      size="sm"
      footer={
        <>
          <Button onClick={() => void copyText(`${t("users.credentials.username")}: ${data.username}\n${t("users.credentials.password")}: ${data.password}`)}>
            {t("users.credentials.copyAll")}
          </Button>
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t("users.credentials.done")}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        {row(t("users.credentials.username"), data.username)}
        {row(t("users.credentials.password"), data.password)}
      </div>
    </Modal>
  );
}

/** Bir nechta tanlov (qo'shimcha filial/jamoalar): belgilanadigan ro'yxat, uzun bo'lsa aylantiriladi */
function CheckList({ options, value, onChange, label, empty }: { options: { id: number; label: string }[]; value: number[]; onChange: (v: number[]) => void; label: string; empty: string }) {
  if (options.length === 0) return <p className="text-[12px] text-muted">{empty}</p>;
  return (
    <div role="group" aria-label={label} className="max-h-40 space-y-1.5 overflow-y-auto rounded-[10px] border border-border px-3 py-2">
      {options.map((o) => (
        <Checkbox
          key={o.id}
          label={o.label}
          checked={value.includes(o.id)}
          onChange={(e) => onChange(e.target.checked ? [...value, o.id] : value.filter((id) => id !== o.id))}
          className="flex"
        />
      ))}
    </div>
  );
}

// ---- Bloklash oynasi (eskiz 22) ----

export function BlockModal({ user, onOpenChange }: { user: AdminUser | null; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation("admin");
  const block = useBlockUser();
  const [mode, setMode] = useState<"one" | "pool">("one");
  const [target, setTarget] = useState("");
  const [notify, setNotify] = useState(true);
  const recruiters = useUserOptions({ role: "recruiter", branch: user?.branch ?? undefined }, !!user);
  const branches = useBranches();
  const branch = branches.data?.results.find((b) => b.id === user?.branch);
  // Filial menejer(lar)i -- bloklanayotgan xodimning o'zidan tashqari
  const managerName = branch?.managers.filter((m) => m.id !== user?.id).map((m) => m.display_name).join(", ") || t("common:roles.branch_manager");

  useEffect(() => {
    if (user) {
      setMode("one");
      setTarget("");
      setNotify(true);
    }
  }, [user]);

  if (!user) return null;
  const candidates = (recruiters.data?.results ?? []).filter((o) => o.id !== user.id);
  const hasDrivers = user.drivers_count > 0;
  const who = `${user.full_name} (${t(`common:roles.${user.role}`)}${user.branch_name ? ` · ${user.branch_name}` : ""})`;

  async function submit() {
    if (!user) return;
    if (hasDrivers && mode === "one" && !target) {
      toast.warning(t("users.blockModal.chooseRecruiterError"));
      return;
    }
    try {
      const res = await block.mutateAsync({ id: user.id, mode: hasDrivers ? mode : "pool", reassign_to: mode === "one" && target ? Number(target) : null });
      toast.success(t("users.blockModal.blocked", { name: user.display_name }), res.drivers_moved ? t("users.blockModal.moved", { count: res.drivers_moved }) : undefined);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("users.blockModal.failed"));
    }
  }

  return (
    <Modal
      open={!!user}
      onOpenChange={onOpenChange}
      title={t("users.blockModal.title", { name: user.display_name })}
      size="md"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="danger" loading={block.isPending} onClick={submit}>
            {hasDrivers ? t("users.blockModal.blockReassign") : t("users.blockModal.block")}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-soft">{t("users.blockModal.body", { name: who })}</p>
      <div className="mt-4">
        <div className="text-[13px] font-semibold">{t("users.blockModal.work", { first: user.first_name })}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge tone="grey">{t("users.blockModal.drivers", { count: user.drivers_count })}</Badge>
        </div>
        {hasDrivers ? (
          <div className="mt-3 space-y-3">
            <label className="flex items-start gap-2 text-[13px]">
              <input type="radio" name="block-mode" className="mt-1 accent-primary" checked={mode === "one"} onChange={() => setMode("one")} />
              <span className="flex-1">
                <span className="font-semibold">{t("users.blockModal.reassignAll")}</span>
                <Select value={target} onChange={(e) => setTarget(e.target.value)} className="mt-2" disabled={mode !== "one"} aria-label={t("common:roles.recruiter")}>
                  <option value="">{t("users.blockModal.chooseRecruiter")}</option>
                  {candidates.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.display_name}
                    </option>
                  ))}
                </Select>
              </span>
            </label>
            <label className="flex items-start gap-2 text-[13px]">
              <input type="radio" name="block-mode" className="mt-1 accent-primary" checked={mode === "pool"} onChange={() => setMode("pool")} />
              <span>{t("users.blockModal.returnToPool", { branch: branch?.name ?? user.branch_name ?? "", manager: managerName })}</span>
            </label>
          </div>
        ) : (
          <p className="mt-2 text-[12px] text-muted">{t("users.blockModal.noDrivers")}</p>
        )}
        <div className="mt-4">
          <Checkbox
            label={mode === "one" && target ? t("users.blockModal.notify", { manager: managerName }) : t("users.blockModal.notifyManager", { manager: managerName })}
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
        </div>
      </div>
    </Modal>
  );
}
